/** @jsx jsx */
import React, { useState, forwardRef } from 'react';
import { styled, css, jsx } from '@compiled/react';
import Popup, { type PopupComponentProps } from '@atlaskit/popup';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { IssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { Deployment } from '../../../../../common/types';
import DeploymentIcon from '../../../../../common/ui/deployment-icon';
import DeploymentsDevPanel from './deployments-dev-panel';
import DeploymentsDialogContent from './dialog-content/main.tsx';

type Props = {
	issueId: IssueId;
	deployments: Deployment[];
	showMultipleDeploymentsIcon: boolean;
	groupIndex: number;
};

// we need a parent for the Popup to make the exit transition work
const UnstyledPopupComponent = forwardRef<HTMLDivElement, PopupComponentProps>((props, ref) => (
	<div css={unstyledPopupContainerStyles} {...props} ref={ref} />
));

const useFireShowMoreInfoAnalytics = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	return () =>
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'showMoreInfoDeployments',
			}),
		);
};

const useFireDeploymentsClickedAnalytics = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	return (groupIndex: number) =>
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'deployments',
			}),
			'deploymentsClicked',
			{ groupIndex },
		);
};

const DeploymentsDialog = ({
	issueId,
	deployments,
	showMultipleDeploymentsIcon,
	groupIndex,
}: Props) => {
	const [showMoreInfo, setShowMoreInfo] = useState(false);
	const [placement, setPlacement] = useState<'bottom' | 'top'>('bottom');
	const fireShowMoreInfoAnalytics = useFireShowMoreInfoAnalytics();
	const [isOpen, setIsOpen] = useState(false);
	const openPopup = () => {
		setIsOpen(true);
	};
	const closePopup = () => {
		setIsOpen(false);
	};
	const fireDeploymentsClickedAnalytics = useFireDeploymentsClickedAnalytics();

	// NOTE: both popup and the DevPanelDetailsDialog modal add a
	// window onclick listener to close themselves. Handle it in Popup onclose prop
	// otherwise changing tabs in the modal sees both modal and popup close.
	return (
		<>
			<Popup
				shouldFlip={false}
				isOpen={isOpen}
				onClose={showMoreInfo ? undefined : closePopup}
				placement={placement}
				autoFocus={false}
				content={() => (
					<DeploymentsDialogContent
						deployments={deployments}
						onShowMoreInfo={() => {
							setShowMoreInfo(true);
							fireShowMoreInfoAnalytics();
						}}
						onClose={closePopup}
					/>
				)}
				trigger={({ ref, ...triggerProps }) => (
					<DeploymentIconButton
						{...triggerProps}
						onClick={(evt) => {
							if (!isOpen) {
								fireDeploymentsClickedAnalytics(groupIndex);
								openPopup();
							} else {
								closePopup();
							}

							// custom logic for placement
							// mouseY is accurate enough to determine if popup should go above or below
							// mouseY is more performant to retreive than getBoundingClientRect()
							const mouseY = evt.clientY;
							const isBottomHalfOfViewport = window.innerHeight - mouseY < window.innerHeight / 2;
							setPlacement(isBottomHalfOfViewport ? 'top' : 'bottom');
						}}
						ref={ref}
					>
						<DeploymentIcon
							deployments={deployments}
							showMultipleDeploymentsIcon={showMultipleDeploymentsIcon}
							hasBorder
						/>
					</DeploymentIconButton>
				)}
				popupComponent={UnstyledPopupComponent}
			/>
			{showMoreInfo && (
				<DeploymentsDevPanel
					issueId={issueId}
					onClose={() => {
						setShowMoreInfo(false);
					}}
				/>
			)}
		</>
	);
};

const unstyledPopupContainerStyles = css({
	'&:focus': {
		outline: 'none',
	},
});

const normalizeButtonStyles = css({
	background: 'transparent',
	border: 'none',
	margin: 0,
	padding: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const DeploymentIconButton = styled.button(normalizeButtonStyles, {
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	cursor: 'pointer',
	overflow: 'hidden',
});

export default DeploymentsDialog;
